import type { V2RelayerMetadata, V2RelayerSigner } from "./client";

/** What a relay is for: a user's action or the automatic return and merges. */
export type V2RelayerLane = "action" | "background";

/** A background relay moves to an action signer only when its own lane has
 * at least this much more queued. */
const BACKGROUND_SPILL_SECONDS = 20;

const quickest = (
  signers: readonly V2RelayerSigner[],
  random: () => number
): V2RelayerSigner => {
  const least = Math.min(...signers.map((signer) => signer.waitSeconds));
  const tied = signers.filter((signer) => signer.waitSeconds === least);
  // Clients read the same estimate at the same moment: a random pick among
  // equally quick signers keeps them from all choosing the same one.
  return tied[Math.min(tied.length - 1, Math.floor(random() * tied.length))];
};

/**
 * Picks the signer for one operation. With several signers the last one
 * carries the automatic returns and merges, so a user's action never waits
 * behind someone else's return; the others share the actions. A proof binds
 * the signer, so the choice holds for the whole operation. With one signer,
 * or no signer list, everything stays on the primary as before.
 */
export const chooseRelayer = (
  metadata: V2RelayerMetadata,
  lane: V2RelayerLane,
  random: () => number = Math.random
): V2RelayerMetadata => {
  const signers = metadata.signers;
  if (!signers || signers.length < 2) return metadata;
  const background = signers[signers.length - 1];
  const actions = signers.slice(0, -1);
  const open = (list: readonly V2RelayerSigner[]) =>
    list.filter((signer) => signer.available);
  let chosen: V2RelayerSigner;
  if (lane === "action") {
    // Only when every action signer is full does an action use the return lane.
    const candidates = open(actions).length
      ? open(actions)
      : open(signers).length
        ? open(signers)
        : actions;
    chosen = quickest(candidates, random);
  } else {
    const spill = open(actions).length
      ? quickest(open(actions), random)
      : undefined;
    chosen =
      spill &&
      (!background.available ||
        background.waitSeconds - spill.waitSeconds >= BACKGROUND_SPILL_SECONDS)
        ? spill
        : background;
  }
  return { ...metadata, address: chosen.address };
};
